import { Capacitor, registerPlugin } from "@capacitor/core";
import { Toast as NativeToast } from "@capacitor/toast";
import { captureException } from "@sentry/react-router";
import { AlertTriangle, Check } from "lucide-react";
import {
	createContext,
	use,
	useCallback,
	useMemo,
	useState
} from "react";
import type { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import { isPluginUnsupported, reportAppOutdated } from "~/capacitor";
import type { IconComponent } from "~/components/icons";
import { NativeNavigationPlugin } from "~/native-navigation";

import { device } from "./use-device";

export type ToastType = "error" | "success" | "warning";
export type ToastDuration = "long" | "short";

export interface CreateToast {
	type?: ToastType;
	icon?: IconComponent;
	duration?: ToastDuration;
	value: string;
}

export interface Toast {
	id: string;
	type: ToastType;
	icon: IconComponent;
	children: string;
	ttl: number | null;
	remove: () => void;
}

export interface ToastContext {
	toasts: Array<Toast>;

	add: (toast: CreateToast | string) => Promise<void>;
	addError: (reason: unknown) => Promise<void>;

	remove: (toast: Pick<Toast, "id">) => void;
}

const ToastContext = createContext<ToastContext>({} as ToastContext);

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
	return use(ToastContext);
}

const ToastItem: React.FC<Omit<Toast, "key">> = ({ type, icon: Icon, children, remove }) => {
	return (
		<button
			className={twMerge(
				"pointer-events-auto flex w-full max-w-xl items-center gap-4 rounded-lg px-6 py-4 text-left shadow-brand-1",
				{
					success: "bg-brand-gradient text-white-20",
					error: "bg-white-10 dark:bg-black-80 border-2 border-red-500 text-black-80 dark:text-white-20 motion-preset-slide-left",
					warning: "bg-black-80 text-white-20 border-2 border-yellow-500"
				}[type]
			)}
			type="button"
			onClick={() => remove()}
		>
			<Icon className="size-5 shrink-0" strokeWidth={2} />
			<span data-mask className="font-montserrat font-semibold">
				{children}
			</span>
		</button>
	);
};

export interface AddErrorOptions {
	expected?: boolean;
}

const Snackbar = registerPlugin<{
	// In milliseconds, and CSS pixels kept clear at the bottom.
	show: (options: { text: string; duration: number; bottom: number; dark?: boolean }) => Promise<void>;
}>("Snackbar");

// iOS's system colours.
const nativeToastIcons = {
	success: { icon: "checkmark.circle.fill", tint: "#34C759" },
	warning: { icon: "exclamationmark.triangle.fill", tint: "#FF9500" },
	error: { icon: "xmark.octagon.fill", tint: "#FF3B30" }
};

export const ToastProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const { t } = useTranslation();
	const [toasts, setToasts] = useState<Array<Toast>>([]);

	const remove = useCallback((toast: Pick<Toast, "id">) => {
		setToasts((toasts) => toasts.filter(({ id }) => id !== toast.id));
	}, []);

	const add = useCallback(
		async (options: CreateToast | string) => {
			if (typeof options === "string") options = { value: options };
			const { type = "success", icon, duration = "short", value } = options;

			// See https://capacitorjs.com/docs/apis/toast
			const ttl = duration === "short" ? 2000 : 3500;

			// The native tab bar would cover Capacitor's toast, so we show our own
			// Liquid Glass toast at the top.
			if (device.nativeNavigation) {
				const shown = await NativeNavigationPlugin.toast({
					text: value,
					...nativeToastIcons[type],
					duration: ttl
				}).then(() => true, () => false);
				if (shown) return;
			}

			if (device.native) {
				const text = type === "success"
					? value
					: type === "warning"
						? `⚠️ ${value}`
						: `🚫 ${value}`;

				// Android toasts only fit 2 lines of text, so use snackbars when available.
				if (device.android && Capacitor.isPluginAvailable("Snackbar")) {
					const tops = [...document.querySelectorAll("header, [data-snackbar-anchor]")]
						.map((element) => element.getBoundingClientRect())
						.filter(({ height, top }) => height > 0 && top > window.innerHeight / 2)
						.map(({ top }) => top);
					const bottom = tops.length > 0 ? window.innerHeight - Math.min(...tops) : 0;

					return Snackbar.show({ text, duration: ttl, bottom, dark: document.body.dataset.theme === "dark" });
				}

				return NativeToast.show({
					duration,
					text,
					// Android only supports bottom position.
					position: "bottom"
				});
			}

			const toast: Toast = {
				id: String(performance.now()),
				type,
				icon: icon || (type === "success" ? Check : AlertTriangle),
				ttl,
				children: value,
				remove: () => {
					remove(toast);
				}
			};

			if (toast.ttl) setTimeout(() => toast.remove(), toast.ttl);
			setToasts((toasts) => [toast, ...toasts]);
		},
		[remove]
	);

	const addError = useCallback(
		async (reason: unknown, { expected = false }: AddErrorOptions = {}) => {
			if (reportAppOutdated(reason)) return;

			const message
				= (typeof reason === "object"
					&& reason !== null
					&& "message" in reason
					&& typeof reason.message === "string"
					&& reason.message)
				|| (typeof reason === "string" && reason)
				|| t("errors.internal_server_error");

			if (!expected && !isPluginUnsupported(reason)) captureException(reason);

			return add({
				type: "error",
				value: message
			});
		},
		[t, add]
	);

	return (
		<ToastContext
			value={useMemo(
				() => ({
					toasts,
					add,
					remove,
					addError
				}),
				[toasts, add, remove, addError]
			)}
		>
			{children}
			{toasts.length > 0 && (
				<div className="pointer-events-none fixed right-0 bottom-16 z-999 flex flex-col-reverse gap-2 p-8 px-4 desktop:top-16 desktop:bottom-[unset] desktop:px-8">
					{toasts.map((toast) => (
						<ToastItem {...toast} key={toast.id} />
					))}
				</div>
			)}
		</ToastContext>
	);
};
